import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { AttributionField } from "./shared/AttributionField";
import { ChangeLog } from "./shared/ChangeLog";
import { imprestCategoryId, isImprestCategory, reshapeForCategory } from "./shared/imprestCategory";
import VendorFormDialog from "../vendorMaster/VendorFormDialog";

function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

// One payment line. Used for creator add/edit (Draft/Sent Back) and the
// Director's pre-approval edit (directorMode) — the Director's endpoint
// additionally records every changed field into the line's audit trail.
export default function LineFormDialog({ listId, line, refData, directorMode, correctionMode, imprestPreset, onClose, onSaved }) {
  useEffect(() => { refData.refresh(["vendors", "sites", "equipment"]); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const isEdit = !!line;
  const todayStr = new Date().toLocaleDateString("en-CA");
  const isPaid = correctionMode && line?.status === "Paid";
  const needsReason = correctionMode && ["Submitted", "Approved", "Paid"].includes(line?.status);
  const [newFile, setNewFile] = useState(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [outstandingBills, setOutstandingBills] = useState([]);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [form, setForm] = useState({
    entity_code: line?.entity_code || "",
    site_id: line?.site_id || "",
    equipment_id: line?.equipment_id || "",
    category_id: line?.category_id || (imprestPreset ? imprestCategoryId(refData.categories) || "" : ""),
    vendor_code: line?.vendor_code || "",
    vendor_bill_id: line?.vendor_bill_id || "",
    bill_amount_applied: line?.bill_amount_applied ?? "",
    amount: line?.amount ?? "",
    purpose: line?.purpose || "",
    payee_type: line?.payee_type || (imprestPreset ? "Employee" : line?.vendor_code ? "Vendor" : "Other"),
    payee_employee_id: line?.payee_employee_id || imprestPreset?.employeeId || "",
    payee_note: line?.payee_note || "",
    payment_date: line?.paid_date || todayStr,
    payment_reference: line?.utr || "",
    reason: "",
  });
  const [payeeTouched, setPayeeTouched] = useState(isEdit);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const isImprest = isImprestCategory(refData.categories, form.category_id);

  useEffect(() => {
    if (!form.vendor_code) {
      setOutstandingBills([]);
      return;
    }
    client
      .get("/payment-desk/bills/for-vendor/outstanding", { params: { vendor_code: form.vendor_code } })
      .then((res) => setOutstandingBills(res.data))
      .catch(() => setOutstandingBills([]));
  }, [form.vendor_code]);

  const selectedBill = outstandingBills.find((b) => b.bill_id === form.vendor_bill_id);
  const selectedVendor = refData.vendors.find((v) => v.vendor_code === form.vendor_code);
  const selectedPayeeEmployee = (refData.payeeEmployees || []).find((e) => e.employee_id === form.payee_employee_id);

  const changeVendor = (code) =>
    setForm((f) => {
      const next = { ...f, vendor_code: code, vendor_bill_id: "" };
      if (!code && f.payee_type === "Vendor") next.payee_type = "Other";
      else if (code && !payeeTouched) next.payee_type = "Vendor";
      return next;
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.entity_code) return toast.error("Entity is required");
    if (!form.category_id) return toast.error("Category is required");
    if (isImprest && !form.payee_employee_id) return toast.error("Employee is required");
    if (!isImprest && !form.site_id && !form.equipment_id) return toast.error("Select a Site or Equipment");
    if (form.amount === "" || Number(form.amount) <= 0) return toast.error("Amount must be greater than 0");
    if (!isImprest && form.payee_type === "Vendor" && !form.vendor_code) return toast.error("Select a Vendor to pay to the Vendor's bank account — or change Paid To");
    if (!isImprest && form.payee_type === "Employee" && !form.payee_employee_id) return toast.error("Select the Employee to pay");
    if (!isImprest && form.vendor_bill_id && (form.bill_amount_applied === "" || Number(form.bill_amount_applied) <= 0)) {
      return toast.error("Amount Applied to the Bill must be greater than 0");
    }
    if (isPaid) {
      if (!form.payment_date) return toast.error("Paid Date is required");
      if (form.payment_date > todayStr) return toast.error("Paid Date cannot be in the future");
    }
    if (needsReason && !form.reason.trim()) return toast.error(`A reason is required to edit a ${line.status} line`);
    setSaving(true);
    const payload = {
      entity_code: form.entity_code,
      site_id: isImprest ? null : form.site_id || null,
      equipment_id: isImprest ? null : form.equipment_id || null,
      category_id: form.category_id,
      vendor_code: isImprest ? null : form.vendor_code || null,
      vendor_bill_id: isImprest ? null : form.vendor_bill_id || null,
      bill_amount_applied: !isImprest && form.vendor_bill_id ? Number(form.bill_amount_applied) : null,
      amount: Number(form.amount),
      purpose: form.purpose.trim() || null,
      payee_type: isImprest ? "Employee" : form.payee_type,
      payee_employee_id: isImprest || form.payee_type === "Employee" ? form.payee_employee_id : null,
      payee_note: !isImprest && form.payee_type === "Other" ? form.payee_note.trim() || null : null,
    };
    try {
      if (correctionMode) {
        const reason = form.reason.trim() || null;
        await client.put(`/payment-desk/lines/${line.id}/correct`, {
          ...payload, reason,
          ...(isPaid ? { payment_date: form.payment_date, payment_reference: form.payment_reference.trim() || null } : {}),
        });
        try {
          if (newFile) {
            const fd = new FormData();
            fd.append("file", newFile);
            await client.post(`/payment-desk/lines/${line.id}/attachment`, fd, { params: { reason } });
          } else if (removeAttachment && line.attachment) {
            await client.delete(`/payment-desk/lines/${line.id}/attachment`, { params: { reason } });
          }
        } catch (err) {
          toast.error(`Fields saved, but the attachment change failed: ${getErrorMessage(err)}`);
          onSaved();
          return;
        }
        toast.success("Entry updated");
      } else if (directorMode) {
        await client.put(`/payment-desk/approval-queue/lines/${line.id}`, payload);
        toast.success("Line updated");
      } else if (isEdit) {
        await client.put(`/payment-desk/payment-lists/${listId}/lines/${line.id}`, payload);
        toast.success("Line updated");
      } else {
        await client.post(`/payment-desk/payment-lists/${listId}/lines`, payload);
        toast.success("Line added");
      }
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="line-form-dialog">
        <DialogHeader>
          <DialogTitle>{correctionMode ? `Edit ${line.status} Payment — ${line.list_id} · Line #${line.line_no}` : directorMode ? `Edit Line #${line.line_no} (Director)` : isEdit ? `Edit Line #${line.line_no}` : "Add Payment Line"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Entity *</label>
              <SearchableSelect testId="line-form-entity-select" loading={refData.loadingLists.entities} value={form.entity_code} onValueChange={(v) => set("entity_code", v)}  placeholder="Select entity"
  options={refData.entities.map((en) => ({ value: en.code, label: en.name, testId: `line-form-entity-option-${en.code}` }))} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Category *</label>
              <SearchableSelect testId="line-form-category-select" loading={refData.loadingLists.categories} value={form.category_id} onValueChange={(v) => setForm((f) => reshapeForCategory(f, refData.categories, v))}  placeholder="Select category"
  options={refData.categories.map((c) => ({ value: c.id, label: c.name, testId: `line-form-category-option-${c.id}` }))} />
            </div>
          </div>

          {!isImprest && <AttributionField
            siteId={form.site_id}
            equipmentId={form.equipment_id}
            sites={refData.sites}
            equipment={refData.equipment} sitesLoading={refData.loadingLists.sites} equipmentLoading={refData.loadingLists.equipment}
            onChange={({ siteId, equipmentId }) => setForm((f) => ({ ...f, site_id: siteId, equipment_id: equipmentId }))}
            testId="line-form-attribution"
          />}

          <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4">
            {isImprest && (
              <div className="col-span-2" data-testid="line-form-imprest-employee-section">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Employee *</label>
                <SearchableSelect testId="line-form-imprest-employee-select" loading={refData.loadingLists.payeeEmployees} value={form.payee_employee_id} onValueChange={(v) => set("payee_employee_id", v)} placeholder="Select employee"
  options={(refData.payeeEmployees || []).map((e) => ({ value: e.employee_id, label: `${e.employee_id} — ${e.name}`, testId: `line-form-imprest-employee-option-${e.employee_id}` }))} />
                <p className="text-xs text-slate-500 mt-1">Imprest advance — counted in this employee's Imprest balance once the line is marked Paid.</p>
                {selectedPayeeEmployee && !selectedPayeeEmployee.bank_complete && (
                  <p className="text-xs text-red-600 mt-1" data-testid="line-form-imprest-bank-warning">
                    Employee {selectedPayeeEmployee.employee_id}'s bank details are incomplete — Accounts cannot mark this line Paid until the Employee record is completed.
                  </p>
                )}
              </div>
            )}
            {!isImprest && <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Vendor {form.vendor_bill_id ? "*" : ""}</label>
              <SearchableSelect testId="line-form-vendor-select" loading={refData.loadingLists.vendors} value={form.vendor_code || "none"} placeholder="None"
  onValueChange={(v) => (v === "__add_vendor__" ? setShowAddVendor(true) : changeVendor(v === "none" ? "" : v))}
  options={[
    { value: "none", label: "None", pinned: true },
    ...refData.vendors.map((v) => ({ value: v.vendor_code, label: v.vendor_name, searchText: v.vendor_code, testId: `line-form-vendor-option-${v.vendor_code}` })),
    ...(true ? [{ value: "__add_vendor__", label: "+ Add New Vendor", pinned: true, className: "text-amber-600 font-medium", testId: "line-form-add-new-vendor" }] : []),
  ]} />
            </div>}
            {!isImprest && <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Linked Vendor Bill (optional)</label>
              <SearchableSelect testId="line-form-bill-select" value={form.vendor_bill_id || "none"} onValueChange={(v) => set("vendor_bill_id", v === "none" ? "" : v)} disabled={!form.vendor_code}
  placeholder={form.vendor_code ? "None" : "Select a Vendor first"} options={[
    { value: "none", label: "None", pinned: true },
    ...outstandingBills.map((b) => ({ value: b.bill_id, label: `${b.bill_id} — ${money(b.outstanding_balance)} outstanding`, testId: `line-form-bill-option-${b.bill_id}` })),
  ]} />
              {selectedBill && (
                <p className="text-xs text-slate-500 mt-1" data-testid="line-form-bill-balance">
                  Bill {selectedBill.bill_id}: {money(selectedBill.amount)} billed · {money(selectedBill.outstanding_balance)} outstanding
                </p>
              )}
            </div>}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Amount (₹) *</label>
              <Input type="number" step="any" data-testid="line-form-amount-input" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
            </div>
            {!isImprest && form.vendor_bill_id && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Amount Applied to Bill (₹) *</label>
                <Input type="number" step="any" data-testid="line-form-applied-input" value={form.bill_amount_applied} onChange={(e) => set("bill_amount_applied", e.target.value)} />
              </div>
            )}
          </div>

          {!isImprest && <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4" data-testid="line-form-paid-to-section">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Paid To *</label>
              <Select
                value={form.payee_type}
                onValueChange={(v) => {
                  setPayeeTouched(true);
                  set("payee_type", v);
                }}
              >
                <SelectTrigger data-testid="line-form-paid-to-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vendor" data-testid="line-form-paid-to-option-vendor">Vendor's bank account</SelectItem>
                  <SelectItem value="Employee" data-testid="line-form-paid-to-option-employee">Employee's bank account</SelectItem>
                  <SelectItem value="Other" data-testid="line-form-paid-to-option-other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.payee_type === "Employee" && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Employee *</label>
                <SearchableSelect testId="line-form-payee-employee-select" loading={refData.loadingLists.payeeEmployees} value={form.payee_employee_id} onValueChange={(v) => set("payee_employee_id", v)} placeholder="Select employee"
  options={(refData.payeeEmployees || []).map((e) => ({ value: e.employee_id, label: `${e.employee_id} — ${e.name}`, testId: `line-form-payee-employee-option-${e.employee_id}` }))} />
              </div>
            )}
            {form.payee_type === "Other" && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Payee Note (optional)</label>
                <Input data-testid="line-form-payee-note-input" placeholder="e.g. name of the person paid" value={form.payee_note} onChange={(e) => set("payee_note", e.target.value)} />
              </div>
            )}
            {form.payee_type === "Vendor" && selectedVendor && !selectedVendor.bank_details_complete && (
              <p className="col-span-2 text-xs text-red-600" data-testid="line-form-payee-bank-warning">
                Vendor {selectedVendor.vendor_name}'s bank details are incomplete — Accounts cannot mark this line Paid until the Vendor record is completed.
              </p>
            )}
            {form.payee_type === "Employee" && selectedPayeeEmployee && !selectedPayeeEmployee.bank_complete && (
              <p className="col-span-2 text-xs text-red-600" data-testid="line-form-payee-bank-warning">
                Employee {selectedPayeeEmployee.employee_id}'s bank details are incomplete — Accounts cannot mark this line Paid until the Employee record is completed.
              </p>
            )}
          </div>}

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Purpose / Remarks</label>
            <Textarea data-testid="line-form-purpose-input" value={form.purpose} onChange={(e) => set("purpose", e.target.value)} />
          </div>

          {correctionMode && (
            <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4" data-testid="line-form-completed-section">
              {isPaid && (
                <>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Paid Date *</label>
                    <Input type="date" max={todayStr} data-testid="line-form-paid-date-input" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">UTR / Reference</label>
                    <Input data-testid="line-form-utr-input" value={form.payment_reference} onChange={(e) => set("payment_reference", e.target.value)} />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Attachment</label>
                {line.attachment && !removeAttachment && !newFile && (
                  <p className="text-xs text-slate-600 mb-1.5" data-testid="line-form-attachment-current">
                    Current: {line.attachment.filename}{" "}
                    <button type="button" className="text-red-600 underline ml-2" data-testid="line-form-attachment-remove" onClick={() => setRemoveAttachment(true)}>Remove</button>
                  </p>
                )}
                {removeAttachment && <p className="text-xs text-amber-600 mb-1.5" data-testid="line-form-attachment-removed">Attachment will be removed on save.</p>}
                <Input type="file" accept="image/*,application/pdf" data-testid="line-form-attachment-input" onChange={(e) => setNewFile(e.target.files?.[0] || null)} />
                <p className="text-xs text-slate-400 mt-1">Replacing or removing keeps the old file in storage and is logged in the change history.</p>
              </div>
              {needsReason && (
                <div className="col-span-2">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Reason for this edit *</label>
                  <Input data-testid="line-form-reason-input" placeholder="One line: why is this being changed?" value={form.reason} onChange={(e) => set("reason", e.target.value)} />
                </div>
              )}
            </div>
          )}

          {correctionMode && <ChangeLog entity={line} testId="line-form-change-log" />}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="line-form-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="line-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
              {saving ? "Saving..." : isEdit || directorMode ? "Save Changes" : "Add Line"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {showAddVendor && (
        <VendorFormDialog
          vendor={null}
          onClose={() => setShowAddVendor(false)}
          onSaved={(newVendor) => {
            setShowAddVendor(false);
            changeVendor(newVendor.vendor_code);
          }}
        />
      )}
    </Dialog>
  );
}
